import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import utils from '../../utils/utils';
import { useDeliveryEdit } from './apiHooks';
import DELIVERY_STATUS from '../../components/constants/deliveryStatus';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import ORDER_PACKING_STATUS from '../../components/constants/orderPackingStatus';
import PackerRefunds from './packerRefunds';
import PackerCounter from './packerCounter';
import BarcodeScanner from './components/barcodeScanner';
import PackerDeliveryOrderBatchOrder from './packerDeliveryOrderBatchOrder';

// TODO order items by packingSector if present
const PackerDeliveryOrderBatchRow = ({ delivery }) => {
  const deliveryOrderBatch = delivery.deliveryOrderBatch;

  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);

  const [showItems, setShowItems] = useState(false);
  const [packedItemIds, setPackedItemIds] = useState([]);
  const [refundOrderItems, setRefundOrderItems] = useState([]);
  const [bagCount, setBagCount] = useState(null);
  const [thermoBoxIds, setThermoBoxIds] = useState(deliveryOrderBatch.thermoBoxIds || '');

  const { editDelivery, isLoading: editLoading, isError: isEditError, error: editError } = useDeliveryEdit(() => {});

  useEffect(() => {
    // reset "Packed" item state on order batch change

    const newPackedItemIds = [];
    const packedOrders = deliveryOrderBatch.orders.filter((o) => o.packingStatus === ORDER_PACKING_STATUS.packed);
    packedOrders.forEach((o) => {
      o.items.forEach((oi) => {
        const existingIndex = packedItemIds.findIndex((pii) => pii.orderId === o.id && pii.itemId === oi.item.id);

        if (existingIndex === -1) {
          newPackedItemIds.push({ itemId: oi.item.id, orderId: o.id });
        }
      });
    });

    setPackedItemIds(newPackedItemIds);
    setBagCount(deliveryOrderBatch.bagCount);
  }, [deliveryOrderBatch.id]);

  const addRefundOrderItems = (itemsToAdd) => {
    const newRefundOrderItems = [...refundOrderItems].concat(itemsToAdd);
    setRefundOrderItems(newRefundOrderItems);
  };

  const removeRefundOrderItemFromIndex = (index) => {
    const newRefundOrderItems = [...refundOrderItems];
    newRefundOrderItems.splice(index, 1);
    setRefundOrderItems(newRefundOrderItems);
  };

  const addPackedItem = (packedItem) => {
    const newPackedItemIds = packedItemIds.slice();
    newPackedItemIds.push(packedItem);
    setPackedItemIds(newPackedItemIds);
  };

  const removePackedItemFromIndex = (index) => {
    const newPackedItemIds = packedItemIds.slice();
    newPackedItemIds.splice(index, 1);
    setPackedItemIds(newPackedItemIds);
  };

  const updateRefundOrderItem = (newRoi, index) => {
    const newRefundOrderItems = [...refundOrderItems];

    // currently you can only change the payer on the orderId level, not per each item
    if (newRoi.payer !== newRefundOrderItems[index].payer) {
      newRefundOrderItems.forEach((roi) => (roi.orderId === newRoi.orderId ? (roi.payer = newRoi.payer) : null));
    }

    newRefundOrderItems[index] = newRoi;
    setRefundOrderItems(newRefundOrderItems);
  };

  const refunds = deliveryOrderBatch.orders
    .map((o) => (o.payment ? o.payment.refunds : []))
    .flat()
    .filter((refund, index, self) => self.findIndex((existingValue) => existingValue.id === refund.id) === index);

  const canCompleteOrder = deliveryOrderBatch.orders.every((order) => {
    const orderItems = order.items.filter((oi) => oi.fulfilledQuantity === null || oi.fulfilledQuantity > 0);

    return orderItems.every((orderItem) =>
      packedItemIds.some(({ itemId, orderId }) => itemId === orderItem.item.id && orderId === order.id)
    );
  });

  const isDeliveryPacked =
    [DELIVERY_STATUS.readyForPickup.name, DELIVERY_STATUS.inProgress.name, DELIVERY_STATUS.finished.name].indexOf(
      delivery.status
    ) !== -1;

  const orderBatchIds = deliveryOrderBatch.orders
    .map((o) => `#${o.batchId}`)
    .filter((value, index, self) => self.indexOf(value) === index);

  deliveryOrderBatch.orders.sort((a, b) => {
    return a.marketplaceName.localeCompare(b.marketplaceName);
  });

  const getOrderItemsForPacking = (order) =>
    order.items.filter((oi) => oi.fulfilledQuantity === null || oi.fulfilledQuantity > 0);

  const isOrderPacked = (order, orderItemsForPacking) => {
    const packingOrderItems = orderItemsForPacking || getOrderItemsForPacking(order);

    return (
      isDeliveryPacked ||
      packingOrderItems.every((orderItem) =>
        packedItemIds.some(({ itemId, orderId }) => itemId === orderItem.item.id && orderId === order.id)
      )
    );
  };

  const prePackedOrders = deliveryOrderBatch.orders.filter((o) => o.packingStatus);
  const notPrePackedOrders = deliveryOrderBatch.orders.filter((o) => !o.packingStatus);

  notPrePackedOrders.sort((a, b) => {
    // moves packed orders below unpacked orders, otherwise sorts alphabetically
    const aIsPacked = isOrderPacked(a);
    const bIsPacked = isOrderPacked(b);

    if (aIsPacked && !bIsPacked) {
      return 1;
    } else if (!aIsPacked && bIsPacked) {
      return -1;
    } else {
      return a.marketplaceName.localeCompare(b.marketplaceName);
    }
  });

  return (
    <div className="m-b-6">
      <Loader show={editLoading} />
      {isEditError ? <ErrorMessage error={utils.getErrorMessageFromResponse(editError)} /> : ''}

      <div className="m-b-3 text-muted">
        <small>
          <FormattedMessage id={'packer.delivery.order.batch.order.ids'} />: {orderBatchIds.join(', ')}
        </small>
      </div>

      {deliveryOrderBatch.note ? (
        <div className="alert alert-info text-left m-b-3">
          <div>
            <strong>
              <FormattedMessage id={'delivery.day.view.note'} />:
            </strong>
          </div>
          <div className="text-small">{deliveryOrderBatch.note}</div>
        </div>
      ) : (
        ''
      )}

      <div className="m-b-6">
        {isDeliveryPacked ? (
          <button className="btn btn-normal-link m-b-2" onClick={() => setShowItems(!showItems)}>
            <FontAwesomeIcon icon={showItems ? faCaretUp : faCaretDown} />{' '}
            <FormattedMessage
              id={showItems ? 'packer.delivery.order.batch.hide.items' : 'packer.delivery.order.batch.show.items'}
            />
          </button>
        ) : (
          ''
        )}

        {(delivery.status !== DELIVERY_STATUS.notStarted.name && !isDeliveryPacked) || showItems ? (
          <>
            {prePackedOrders.map((order) => {
              return (
                <PackerDeliveryOrderBatchOrder
                  key={order.id}
                  order={order}
                  isPacked={true}
                  packedItemIds={packedItemIds}
                  addPackedItem={addPackedItem}
                  removePackedItemFromIndex={removePackedItemFromIndex}
                  refundOrderItems={refundOrderItems}
                  addRefundOrderItems={addRefundOrderItems}
                  removeRefundOrderItem={removeRefundOrderItemFromIndex}
                />
              );
            })}

            {notPrePackedOrders.map((order) => {
              const orderItemsForPacking = getOrderItemsForPacking(order);
              const isPacked = isOrderPacked(order, orderItemsForPacking);

              return (
                <PackerDeliveryOrderBatchOrder
                  key={order.id}
                  order={order}
                  orderItemsForPacking={orderItemsForPacking}
                  isPacked={isPacked}
                  packedItemIds={packedItemIds}
                  addPackedItem={addPackedItem}
                  removePackedItemFromIndex={removePackedItemFromIndex}
                  refundOrderItems={refundOrderItems}
                  addRefundOrderItems={addRefundOrderItems}
                  removeRefundOrderItem={removeRefundOrderItemFromIndex}
                />
              );
            })}

            {refundOrderItems.length || refunds.length ? (
              <>
                <hr />

                <PackerRefunds
                  refundOrderItems={refundOrderItems}
                  setRefundOrderItems={setRefundOrderItems}
                  removeFromRefunds={removeRefundOrderItemFromIndex}
                  updateRefundOrderItem={updateRefundOrderItem}
                  orderBatchId={deliveryOrderBatch.orders[0].batchId}
                  user={deliveryOrderBatch.user}
                  refunds={refunds}
                  invalidateQueryKey={'route'}
                />
              </>
            ) : (
              ''
            )}
          </>
        ) : (
          ''
        )}
      </div>

      {!refundOrderItems.length ? (
        <div className="m-b-2">
          {delivery.status !== DELIVERY_STATUS.notStarted.name ? (
            <div className="row">
              <div className="col-12">
                <div className="m-b-2">
                  <div className="m-b-1">
                    <FormattedMessage id={`order.batch.packing.mode.bag.count`} />
                  </div>
                  <PackerCounter
                    count={bagCount}
                    onDecrease={() => setBagCount(bagCount - 1)}
                    onIncrease={() => setBagCount(bagCount + 1)}
                    onReset={() => setBagCount(0)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group field field-string">
                  <label className="control-label">
                    <FormattedMessage id={'order.batch.packing.mode.thermo.box.ids'} />
                  </label>
                  <input
                    className="form-control"
                    placeholder=""
                    type="text"
                    value={thermoBoxIds}
                    onChange={(e) => setThermoBoxIds(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            ''
          )}

          {delivery.status === DELIVERY_STATUS.notStarted.name ? (
            <button
              className={`btn btn-primary m-r-2 ${editLoading ? 'btn-loading' : ''}`}
              onClick={() =>
                editDelivery({
                  delivery: { ...delivery, status: DELIVERY_STATUS.inPacking.name },
                })
              }
            >
              <FormattedMessage id={`order.batch.packing.mode.start`} />
            </button>
          ) : isDeliveryPacked ? (
            <>
              <button
                className={`btn btn-primary btn-small m-r-2 ${editLoading ? 'btn-loading' : ''}`}
                onClick={() =>
                  editDelivery({
                    delivery: { ...delivery, deliveryOrderBatch: { ...deliveryOrderBatch, bagCount, thermoBoxIds } },
                  })
                }
              >
                <FormattedMessage id={`order.batch.packing.mode.edit`} />
              </button>
              <button
                className={`btn btn-default btn-small m-r-2 ${editLoading ? 'btn-loading' : ''}`}
                onClick={() =>
                  editDelivery({
                    delivery: { ...delivery, status: DELIVERY_STATUS.inPacking.name },
                  })
                }
              >
                <FormattedMessage id={`order.batch.packing.mode.close`} />
              </button>
            </>
          ) : (
            <button
              disabled={!canCompleteOrder}
              className={`btn btn-primary ${editLoading ? 'btn-loading' : ''}`}
              onClick={() => {
                editDelivery({
                  delivery: {
                    ...delivery,
                    status: DELIVERY_STATUS.readyForPickup.name,
                    deliveryOrderBatch: { ...deliveryOrderBatch, bagCount, thermoBoxIds },
                  },
                });
              }}
            >
              <FormattedMessage id={`order.batch.packing.mode.done`} />
            </button>
          )}
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

PackerDeliveryOrderBatchRow.propTypes = {
  delivery: PropTypes.object.isRequired,
};

PackerDeliveryOrderBatchRow.defaultProps = {};

export default PackerDeliveryOrderBatchRow;
